import { placerAupresDuPersonnage } from './placement';
import { motsDeLaPhrase, phraseNeedles, scoreTexte } from './ciblage';

export type RiskLevel = 'low' | 'medium' | 'high';

type ActionHandler = (payload?: Record<string, unknown>) => void | Promise<void>;

type RegisteredAction = {
  handler: ActionHandler;
  risk: RiskLevel;
  label: string;
};

const registry = new Map<string, RegisteredAction>();
let markNodes: HTMLElement[] = [];
let markTimer: ReturnType<typeof setTimeout> | null = null;

export function registerAction(
  name: string,
  handler: ActionHandler,
  options?: { risk?: RiskLevel; label?: string }
) {
  registry.set(name, {
    handler,
    risk: options?.risk || 'low',
    label: options?.label || name,
  });
}

export async function runRegisteredAction(
  name: string,
  payload?: Record<string, unknown>
): Promise<boolean> {
  const entry = registry.get(name);
  if (!entry) return false;
  const ok = await confirmIfNeeded(entry.risk, entry.label);
  if (!ok) return false;
  await entry.handler(payload);
  return true;
}

export function isStudioEmbed(): boolean {
  return (window as Window & { __GENIOUS_EMBED__?: string }).__GENIOUS_EMBED__ === 'studio';
}

/**
 * Le cadre où l'aperçu du Studio affiche le site du client.
 *
 * Repéré par `data-genious-apercu`, posé exprès par le Studio. Le repérer par
 * son titre cassait au premier changement de libellé (lot accessibilité) ; les
 * deux anciens sélecteurs restent pour un Studio pas encore redéployé.
 */
export function cadreApercu(): HTMLIFrameElement | null {
  return (
    (document.querySelector('iframe[data-genious-apercu]') as HTMLIFrameElement | null) ||
    (document.querySelector('iframe[title="Prévisualisation site client"]') as HTMLIFrameElement | null) ||
    (document.querySelector('.shell iframe') as HTMLIFrameElement | null)
  );
}

export function scrollTo(selector: string) {
  const el = document.querySelector(selector);
  if (!el) return;
  guideScrollToElement(el);
}

let overlayTracker: (() => void) | null = null;

/**
 * Overlay sans muter le DOM (compatible SPA / Studio / React).
 * Suit l'élément pendant le scroll et le redimensionnement, puis s'abandonne
 * proprement si l'élément disparaît (cas SPA).
 */
function highlightOverlay(el: Element, ms = 6000): HTMLElement {
  clearMarks();
  const overlay = document.createElement('div');
  overlay.setAttribute('data-genious-highlight', '1');
  Object.assign(overlay.style, {
    position: 'fixed',
    borderRadius: '8px',
    border: '2px solid rgba(45,212,191,0.85)',
    boxShadow: '0 0 0 4px rgba(45,212,191,0.2), 0 12px 40px rgba(0,0,0,0.25)',
    background: 'rgba(45,212,191,0.08)',
    pointerEvents: 'none',
    zIndex: '2147483644',
    transition: 'opacity 0.25s ease',
  });
  document.documentElement.appendChild(overlay);
  markNodes.push(overlay);

  const reposition = () => {
    if (!el.isConnected) {
      clearMarks();
      return;
    }
    const r = el.getBoundingClientRect();
    if (r.width < 1 && r.height < 1) return;
    overlay.style.left = `${Math.max(0, r.left - 4)}px`;
    overlay.style.top = `${Math.max(0, r.top - 4)}px`;
    overlay.style.width = `${Math.min(window.innerWidth, r.width + 8)}px`;
    overlay.style.height = `${Math.min(window.innerHeight, r.height + 8)}px`;
  };

  reposition();
  guideScrollToElement(el);
  // Repositionnements pendant l'animation de scroll.
  for (const delay of [120, 280, 460, 700]) window.setTimeout(reposition, delay);

  window.addEventListener('scroll', reposition, { passive: true });
  window.addEventListener('resize', reposition, { passive: true });
  overlayTracker = () => {
    window.removeEventListener('scroll', reposition);
    window.removeEventListener('resize', reposition);
  };

  markTimer = setTimeout(() => clearMarks(), ms);
  return overlay;
}

function climbReadable(el: HTMLElement, root: HTMLElement): HTMLElement {
  let cur = el;
  for (let up = 0; up < 4; up++) {
    const p = cur.parentElement;
    if (!p || p === root || p.tagName === 'BODY') break;
    if (/^(P|H1|H2|H3|H4|LI|BLOCKQUOTE|SECTION|ARTICLE|DIV)$/.test(p.tagName)) {
      cur = p;
      if (p.tagName !== 'DIV') break;
    } else break;
  }
  return cur;
}

/**
 * Trouve l'élément le plus pertinent contenant une phrase, sans muter le DOM.
 * Accepte aussi les tokens non contigüs (ex. « studio 107 » ↔ « studio design : le 107 »).
 */
export function findElementByPhrase(root: HTMLElement, phrase?: string): HTMLElement | null {
  const needles = phraseNeedles(phrase);
  const tokens = motsDeLaPhrase(phrase);

  if (!needles.length && !tokens.length) return null;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null = walker.nextNode();
  let best: { el: HTMLElement; score: number } | null = null;

  while (node) {
    const text = (node.textContent || '').replace(/\s+/g, ' ').toLowerCase();
    const parent = node.parentElement;
    if (!parent || parent.closest('#genious-root') || text.trim().length < 3) {
      node = walker.nextNode();
      continue;
    }

    let score = scoreTexte(text, needles, tokens);

    // Un texte non rendu (accordéon fermé, onglet masqué) ne peut pas être montré :
    // défiler vers lui laisserait le visiteur devant un espace vide. Vérifié sur les
    // seuls candidats, pour ne pas mesurer chaque nœud de la page.
    if (score > 0 && parent.getClientRects().length === 0) score = 0;

    if (score > 0) {
      const tag = parent.tagName.toLowerCase();
      if (/^(h1|h2|h3|h4|p|li|blockquote|section|article)$/.test(tag)) score += 20;
      if (tag === 'a' || tag === 'nav' || parent.closest('nav, header, footer')) score -= 30;
      const el = climbReadable(parent, root);
      if (!best || score > best.score) best = { el, score };
    }
    node = walker.nextNode();
  }

  // Deuxième passe : sur les blocs (p/h/li/section) pour tokens éparpillés dans des enfants.
  if ((!best || best.score < 80) && tokens.length >= 2) {
    const blocks = root.querySelectorAll('p, h1, h2, h3, h4, li, section, article, div');
    for (const block of Array.from(blocks)) {
      if (!(block instanceof HTMLElement) || block.closest('#genious-root')) continue;
      const text = (block.innerText || '').replace(/\s+/g, ' ').toLowerCase();
      if (text.length < 8 || text.length > 1200) continue;
      if (block.getClientRects().length === 0) continue;
      const hit = tokens.filter((t) => text.includes(t)).length;
      if (hit < Math.min(2, tokens.length)) continue;
      if (hit < Math.ceil(tokens.length * 0.6)) continue;
      let score = 60 + hit * 10;
      if (/^(H1|H2|H3|H4|P|LI|SECTION|ARTICLE)$/.test(block.tagName)) score += 15;
      if (block.closest('nav, header, footer')) score -= 30;
      if (!best || score > best.score) best = { el: block, score };
    }
  }

  return best?.el ?? null;
}

/**
 * Surlignage NON destructif : overlay positionné par-dessus l'élément cible.
 * Ne modifie jamais le DOM hôte (compatible SPA React/Vue, Studio, etc.).
 */
export function highlightPhrase(opts: {
  selector?: string;
  phrase?: string;
  ms?: number;
}): HTMLElement | null {
  clearMarks();
  const root = opts.selector
    ? (document.querySelector(opts.selector) as HTMLElement | null)
    : document.body;
  if (!root) return null;

  const target =
    (opts.selector ? root : findElementByPhrase(document.body, opts.phrase)) || root;
  if (!target) return null;
  return highlightOverlay(target, opts.ms ?? 6000);
}

/** Surligne un élément déjà résolu (overlay non destructif + scroll). */
export function highlightEl(el: Element, ms = 6500): HTMLElement {
  return highlightOverlay(el, ms);
}

/**
 * Scroll de guidage "pro" :
 * - atterrissage doux un peu AVANT la cible
 * - marge plus généreuse sur mobile pour laisser voir la section.
 */
export function guideScrollToElement(el: Element) {
  const rect = el.getBoundingClientRect();
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const vh = window.innerHeight;
  const topGap = isMobile ? Math.round(vh * 0.22) : Math.round(vh * 0.18);
  const y = Math.max(0, window.scrollY + rect.top - topGap);
  window.scrollTo({ top: y, behavior: 'smooth' });
}

/**
 * Résout l'élément cible d'une action de guidage. La phrase est prioritaire
 * (recherche textuelle précise), le sélecteur sert de repli.
 */
export function resolveTargetElement(opts: {
  selector?: string;
  phrase?: string;
}): HTMLElement | null {
  if (opts.phrase && opts.phrase.trim().length >= 3) {
    const byPhrase = findElementByPhrase(document.body, opts.phrase);
    if (byPhrase) return byPhrase;
  }
  if (opts.selector) {
    try {
      const el = document.querySelector(opts.selector) as HTMLElement | null;
      if (el && !el.closest('#genious-root')) return el;
    } catch {
      /* sélecteur invalide */
    }
  }
  return null;
}

export function highlightElement(selector: string, ms = 4500) {
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return;
  highlightPhrase({ selector, phrase: el.innerText, ms });
}

export async function pointTo(
  selector: string,
  opts?: { confirm?: boolean; phrase?: string }
): Promise<boolean> {
  const el = document.querySelector(selector);
  if (!el) return false;
  if (opts?.confirm !== false) {
    const ok = await confirmIfNeeded('medium', `Pointer l’élément ${selector}`);
    if (!ok) return false;
  }
  scrollTo(selector);
  highlightPhrase({
    selector,
    phrase: opts?.phrase || (el as HTMLElement).innerText,
    ms: 6500,
  });
  return true;
}

/**
 * Navigation soft en Studio : jamais quitter app.genious-media.com.
 * Si possible, charge l’URL dans l’iframe preview ; sinon no-op (false).
 */
export function navigateTo(href: string): boolean {
  try {
    const url = new URL(href, window.location.href);

    if (isStudioEmbed()) {
      if (url.hash && url.origin === window.location.origin) {
        const id = decodeURIComponent(url.hash.slice(1));
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          highlightOverlay(el);
          return true;
        }
      }
      // Liens Studio (même origin) → bloqués pour ne pas détruire la preview
      if (url.origin === window.location.origin) return false;

      const iframe = cadreApercu();
      if (iframe) {
        iframe.src = url.href;
        return true;
      }
      return false;
    }

    if (url.origin !== window.location.origin) return false;
    if (url.pathname === window.location.pathname && url.search === window.location.search) {
      if (url.hash) {
        const id = decodeURIComponent(url.hash.slice(1));
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          highlightPhrase({ phrase: el.innerText });
        }
      }
      return true;
    }
    window.location.assign(url.href);
    return true;
  } catch {
    return false;
  }
}

export function clearMarks() {
  if (markTimer) clearTimeout(markTimer);
  markTimer = null;
  if (overlayTracker) {
    overlayTracker();
    overlayTracker = null;
  }
  for (const node of markNodes) {
    if (node.getAttribute('data-genious-highlight') === '1') {
      node.remove();
      continue;
    }
    if (node.dataset.geniousOriginal != null) {
      node.innerHTML = node.dataset.geniousOriginal;
      delete node.dataset.geniousOriginal;
      continue;
    }
    if (node.tagName === 'MARK') {
      const parent = node.parentNode;
      if (!parent) continue;
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
      parent.normalize();
    }
  }
  markNodes = [];
  document.querySelectorAll('mark[data-genious-mark]').forEach((m) => {
    const parent = m.parentNode;
    if (!parent) return;
    while (m.firstChild) parent.insertBefore(m.firstChild, m);
    parent.removeChild(m);
    parent.normalize();
  });
  document.querySelectorAll('[data-genious-highlight]').forEach((n) => n.remove());
}

function confirmIfNeeded(risk: RiskLevel, label: string): Promise<boolean> {
  if (risk === 'low') return Promise.resolve(true);
  const message =
    risk === 'high'
      ? `GENIOUS souhaite exécuter une action sensible : « ${label} ». Confirmer ?`
      : `GENIOUS souhaite : « ${label} ». Continuer ?`;
  return Promise.resolve(window.confirm(message));
}

export function listActions() {
  return Array.from(registry.keys());
}

export function placeAwayFromRect(
  wrap: HTMLElement,
  target: DOMRect,
  bubbleW = 280,
  bubbleH = 120
): { left: number; top: number; bubbleSide: 'left' | 'right' } {
  const margin = 16;
  const charW = 72;
  const charH = 86;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  if (isMobile) {
    // Mobile: on garde le GENIOUS visible sans masquer la zone utile.
    const topSlot = target.top > vh * 0.45;
    const left = Math.min(vw - charW - margin, Math.max(margin, vw - charW - 14));
    const top = topSlot ? margin + 6 : Math.max(margin, vh - charH - 110);
    wrap.style.left = `${left}px`;
    wrap.style.top = `${top}px`;
    wrap.style.right = 'auto';
    wrap.style.bottom = 'auto';
    return { left, top, bubbleSide: topSlot ? 'right' : 'left' };
  }

  // Desktop: ancrage éditorial -> aligné verticalement à la section,
  // positionné en centre-droit pour une présence pro et constante.
  const preferredLeft = Math.min(vw - charW - margin, Math.max(Math.round(vw * 0.62), target.right + 14));
  const preferredTop = Math.min(
    vh - charH - margin,
    Math.max(margin, target.top + target.height / 2 - charH / 2)
  );

  type Candidate = {
    left: number;
    top: number;
    bubbleSide: 'left' | 'right';
    score: number;
  };

  const candidates: Candidate[] = [
    {
      left: preferredLeft,
      top: preferredTop,
      bubbleSide: 'left',
      score: 0,
    },
    {
      left: Math.min(vw - charW - margin, target.right + 18),
      top: Math.min(vh - charH - margin, Math.max(margin, target.top + target.height / 2 - charH / 2)),
      bubbleSide: 'left',
      score: 0,
    },
    {
      left: Math.max(margin, target.left - charW - bubbleW - 24),
      top: Math.min(vh - charH - margin, Math.max(margin, target.top + target.height / 2 - charH / 2)),
      bubbleSide: 'right',
      score: 0,
    },
    {
      left: Math.min(vw - charW - margin, Math.max(margin, target.left + target.width / 2 - charW / 2)),
      top: Math.max(margin, target.top - charH - bubbleH - 20),
      bubbleSide: 'left',
      score: 0,
    },
    {
      left: Math.min(vw - charW - margin, Math.max(margin, target.left + target.width / 2 - charW / 2)),
      top: Math.min(vh - charH - margin, target.bottom + 18),
      bubbleSide: 'left',
      score: 0,
    },
  ];

  /** Deux rectangles se touchent-ils, à 10 px près ? */
  function recouvre(r: { left: number; right: number; top: number; bottom: number }) {
    return !(
      r.right < target.left - 10 ||
      r.left > target.right + 10 ||
      r.bottom < target.top - 10 ||
      r.top > target.bottom + 10
    );
  }

  /**
   * Le personnage ou sa bulle cacheraient-ils la cible ?
   *
   * La bulle est évaluée là où `ancrer()` la placera réellement — le même calcul
   * que le rendu. L'ancienne estimation la supposait SOUS le personnage alors
   * qu'elle pousse vers le haut, et s'étendait des deux côtés à la fois : le
   * personnage évitait une bulle imaginaire et cachait la cible avec la vraie.
   */
  function overlaps(a: Candidate) {
    const bulle = placerAupresDuPersonnage(
      { left: a.left, top: a.top, largeur: charW, hauteur: charH },
      { largeur: bubbleW, hauteur: bubbleH },
      { largeur: vw, hauteur: vh },
      { cote: a.bubbleSide === 'left' ? 'gauche' : 'droite', decalageBas: 10 }
    );
    return (
      recouvre({ left: a.left, right: a.left + charW, top: a.top, bottom: a.top + charH }) ||
      recouvre({
        left: bulle.left,
        right: bulle.left + bubbleW,
        top: bulle.top,
        bottom: bulle.top + (bulle.maxHauteur ?? bubbleH),
      })
    );
  }

  for (const c of candidates) {
    c.score = overlaps(c) ? 0 : 10;
    if (c.left === preferredLeft) c.score += 4;
    if (c.left < margin || c.top < margin || c.left > vw - charW || c.top > vh - charH) {
      c.score -= 5;
    }
  }
  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0]!;
  wrap.style.left = `${best.left}px`;
  wrap.style.top = `${best.top}px`;
  wrap.style.right = 'auto';
  wrap.style.bottom = 'auto';
  return { left: best.left, top: best.top, bubbleSide: best.bubbleSide };
}
